// Pure normalizers for the Domino Run goals and the day-plan grid.
//
// These live outside App.jsx for one reason: they are the two functions a snapshot
// and a write both run, so they are the two functions that decide whether the
// Firestore cycle reaches a fixed point. That property is only testable if it can be
// imported. See scripts/test-goal-plan-sync.mjs.
//
// The rule these must obey (learned the expensive way in v2.0.8): normalize(x) must
// equal normalize(normalize(x)). A normalizer that adds a field the raw entry lacks
// makes every snapshot read as "changed", every change trigger a write, and every
// write produce another snapshot — a quota outage in about an hour.

// --- PLAN TIMELINE (fixed 6:30 AM → 11:00 PM, 30-min grid) ---
export const PLAN_START_MIN = 6 * 60 + 30;                 // 6:30 AM
export const PLAN_END_MIN = 23 * 60;                       // 11:00 PM
export const PLAN_TOTAL_MIN = PLAN_END_MIN - PLAN_START_MIN; // 990 min (16.5 h)
export const PLAN_SLOT_MIN = 30;
export const PLAN_SLOT_COUNT = PLAN_TOTAL_MIN / PLAN_SLOT_MIN; // 33

let planSegSeq = 0;
export const makeSegId = () =>
  `seg-${Date.now().toString(36)}-${(planSegSeq++).toString(36)}-${Math.random().toString(36).slice(2, 6)}`;

// Marks a block the AI derived from one of the user's pending quests. Only these
// carry a verdict/remark, so the prefix is what the timeline keys its badge off.
export const AI_QUEST_PREFIX = 'AI Quest:';
export const isAiQuestBlock = (text) =>
  typeof text === 'string' && text.trim().startsWith(AI_QUEST_PREFIX);

const VERDICTS = ['do', 'swap'];

// Every segment leaves here with the same key set, whatever shape came in — a
// segment saved before AI verdicts existed, a legacy { blocks: {...} } day, or a
// tail slot this function padded on. Uneven key sets across those paths is the
// fixed-point hazard, so there is exactly one place that builds a segment.
const buildSegment = ({ id, text, duration, verdict, remark } = {}) => ({
  id: typeof id === 'string' && id ? id : makeSegId(),
  text: typeof text === 'string' ? text : '',
  // Any multiple of 30 min, floor 30 — blocks can merge into bigger ones.
  duration: Number.isFinite(Number(duration)) && Number(duration) >= PLAN_SLOT_MIN
    ? Math.round(Number(duration) / PLAN_SLOT_MIN) * PLAN_SLOT_MIN
    : PLAN_SLOT_MIN,
  // The AI's call on a quest-derived block. Normalized here too, or it is stripped
  // on the round-trip through Firebase and the badge vanishes on the other device.
  verdict: VERDICTS.includes(verdict) ? verdict : '',
  remark: typeof remark === 'string' ? remark : ''
});

// Empty full-day grid. Deterministic ids when a dateKey is given so unsaved days
// don't remount / lose focus on every render.
export const defaultPlanSegments = (dateKey) =>
  Array.from({ length: PLAN_SLOT_COUNT }, (_, i) =>
    buildSegment({ id: dateKey ? `${dateKey}#${i}` : undefined }));

// Coerce any input into a valid segment list whose durations sum to exactly
// PLAN_TOTAL_MIN, so the day always spans 6:30 AM → 11:00 PM.
export const normalizePlanSegments = (input, legacyBlocks) => {
  let segs = [];
  if (Array.isArray(input) && input.length) {
    segs = input.filter((s) => s && typeof s === 'object').map(buildSegment);
  }
  // Legacy migration: old { blocks: { "0": text } } → sequential 30-min segments
  if (!segs.length && legacyBlocks && typeof legacyBlocks === 'object' && !Array.isArray(legacyBlocks)) {
    segs = Object.keys(legacyBlocks)
      .filter((k) => /^\d+$/.test(k))
      .sort((a, b) => Number(a) - Number(b))
      .map((k) => buildSegment({ text: legacyBlocks[k], duration: PLAN_SLOT_MIN }));
  }
  if (!segs.length) return defaultPlanSegments();

  let total = segs.reduce((sum, s) => sum + s.duration, 0);
  // Too long → reclaim from the tail (shrink the last block, else drop it).
  while (total > PLAN_TOTAL_MIN && segs.length) {
    const last = segs[segs.length - 1];
    if (last.duration > PLAN_SLOT_MIN) { last.duration -= PLAN_SLOT_MIN; }
    else { segs.pop(); }
    total -= PLAN_SLOT_MIN;
  }
  // Too short → pad the tail with empty 30-min slots.
  while (total < PLAN_TOTAL_MIN) {
    segs.push(buildSegment({ duration: PLAN_SLOT_MIN }));
    total += PLAN_SLOT_MIN;
  }
  return segs;
};

export const normalizeDayPlans = (input) => {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return {};
  const safe = {};
  Object.entries(input).forEach(([dateKey, entry]) => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return;
    // Only accept YYYY-MM-DD style keys so bad data doesn't poison Firestore
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dateKey))) return;
    safe[dateKey] = {
      focus: typeof entry.focus === 'string' ? entry.focus : '',
      notes: typeof entry.notes === 'string' ? entry.notes : '',
      segments: normalizePlanSegments(entry.segments, entry.blocks),
      aiSource: ['gemini', 'deepseek'].includes(entry.aiSource) ? entry.aiSource : '',
      aiUpdatedAt: typeof entry.aiUpdatedAt === 'string' ? entry.aiUpdatedAt : null
    };
  });
  return safe;
};

// --- DOMINO RUN GOALS ---

// The ladder used to start at a 3-year domino, so that text IS the user's goal
// statement. Lift it into the new `goal` field once rather than losing it when the
// 3-year / 2-year circles stopped being rendered.
//
// Runs on the RAW row, before defaults are merged in, and only when the row has no
// `goal` key at all. Both halves matter: after the merge every row carries
// `goal: ''`, so an absent key and a deliberately cleared goal look identical — and
// falling back on an empty goal would re-derive it from `year1`, which now holds the
// AI's horizon text. Clearing a goal on one device would resurrect it on both.
export const migrateDominoGoals = (rows) => {
  if (!rows || typeof rows !== 'object' || Array.isArray(rows)) return rows;
  const out = {};
  Object.entries(rows).forEach(([cat, row]) => {
    if (!row || typeof row !== 'object' || Array.isArray(row)) { out[cat] = row; return; }
    if (Object.prototype.hasOwnProperty.call(row, 'goal')) { out[cat] = row; return; }
    const legacy = [row.year3, row.year2, row.year1].find((v) => typeof v === 'string' && v.trim());
    out[cat] = { ...row, goal: legacy ? legacy.trim() : '' };
  });
  return out;
};
